// src/components/Admin/DataTable.tsx
import React, { useState } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight,
  Search,
  Filter,
  Download
} from 'lucide-react';

export interface Column<T = Record<string, unknown>> {
  key: string;
  title: string;
  render?: (value: unknown, record: T) => React.ReactNode;
  sortable?: boolean;
  searchable?: boolean;
  width?: string;
}

export interface DataTableProps<T = Record<string, unknown>> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  pagination?: {
    current: number;
    total: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
  };
  selection?: {
    selectedKeys: string[];
    onChange: (keys: string[]) => void;
    getRowKey: (record: T) => string;
  };
  actions?: {
    title: string;
    key: string;
    render: (record: T) => React.ReactNode;
  }[];
  searchPlaceholder?: string;
  onSearch?: (value: string) => void;
  bulkActions?: {
    label: string;
    key: string;
    onClick: (selectedKeys: string[]) => void;
    danger?: boolean;
  }[];
  emptyText?: string;
}

const DataTable = <T extends Record<string, unknown>>({
  columns,
  data,
  loading = false,
  pagination,
  selection,
  actions,
  searchPlaceholder = "Search...",
  onSearch,
  bulkActions,
  emptyText = "No data available"
}: DataTableProps<T>) => {
  const [searchValue, setSearchValue] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  const handleSelectAll = (checked: boolean) => {
    if (!selection) return;
    
    if (checked) {
      const allKeys = data.map(item => selection.getRowKey(item));
      selection.onChange(allKeys);
    } else {
      selection.onChange([]);
    }
  };

  const handleSelectRow = (rowKey: string, checked: boolean) => {
    if (!selection) return;
    
    const newSelectedKeys = checked
      ? [...selection.selectedKeys, rowKey]
      : selection.selectedKeys.filter(key => key !== rowKey);
    
    selection.onChange(newSelectedKeys);
  };

  const handleSort = (columnKey: string) => {
    const direction = sortConfig?.key === columnKey && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key: columnKey, direction });
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    onSearch?.(value);
  };

  const isAllSelected = selection && data.length > 0 && 
    data.every(item => selection.selectedKeys.includes(selection.getRowKey(item)));
  
  const isIndeterminate = selection && selection.selectedKeys.length > 0 && !isAllSelected;

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            {onSearch && (
              <div className="relative w-full sm:w-auto">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full sm:w-64 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            )}
            
            {/* Bulk Actions */}
            {selection && selection.selectedKeys.length > 0 && bulkActions && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-600">
                  {selection.selectedKeys.length} selected
                </span>
                {bulkActions.map(action => (
                  <button
                    key={action.key}
                    onClick={() => action.onClick(selection.selectedKeys)}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      action.danger
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    }`}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2 self-end sm:self-auto">
            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
              <Filter className="h-4 w-4" />
            </button>
            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-container scrollbar-thin">
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {selection && (
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8 sm:w-12">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = !!isIndeterminate;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                </th>
              )}
              
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                  } ${column.width ? column.width : ''}`}
                  onClick={column.sortable ? () => handleSort(column.key) : undefined}
                >
                  <div className="flex items-center space-x-1">
                    <span className="truncate">{column.title}</span>
                    {column.sortable && (
                      <span className="text-gray-400 flex-shrink-0">
                        {sortConfig?.key === column.key ? (
                          sortConfig.direction === 'asc' ? '↑' : '↓'
                        ) : '↕'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              
              {actions && actions.length > 0 && (
                <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-16 sm:w-20">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={columns.length + (selection ? 1 : 0) + (actions ? 1 : 0)} className="px-3 sm:px-6 py-12 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selection ? 1 : 0) + (actions ? 1 : 0)} className="px-3 sm:px-6 py-12 text-center text-gray-500">
                  {emptyText}
                </td>
              </tr>
            ) : (
              data.map((item, index) => {
                const rowKey = selection ? selection.getRowKey(item) : index.toString();
                const isSelected = selection ? selection.selectedKeys.includes(rowKey) : false;
                
                return (
                  <tr
                    key={rowKey}
                    className={`hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-orange-50' : ''
                    }`}
                  >
                    {selection && (
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectRow(rowKey, e.target.checked)}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                      </td>
                    )}
                    
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className="px-3 sm:px-6 py-4 text-sm text-gray-900"
                        style={{ minWidth: '120px' }}
                      >
                        <div className="truncate">
                          {column.render 
                            ? column.render(item[column.key], item)
                            : String(item[column.key] ?? '')
                          }
                        </div>
                      </td>
                    ))}
                    
                    {actions && actions.length > 0 && (
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                          {actions.map(action => (
                            <div key={action.key}>
                              {action.render(item)}
                            </div>
                          ))}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between border-t border-gray-200">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              disabled={pagination.current === 1}
              onClick={() => pagination.onPageChange(pagination.current - 1)}
              className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              disabled={pagination.current * pagination.pageSize >= pagination.total}
              onClick={() => pagination.onPageChange(pagination.current + 1)}
              className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">
                  {((pagination.current - 1) * pagination.pageSize) + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(pagination.current * pagination.pageSize, pagination.total)}
                </span>{' '}
                of{' '}
                <span className="font-medium">{pagination.total}</span> results
              </p>
            </div>
            
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  disabled={pagination.current === 1}
                  onClick={() => pagination.onPageChange(1)}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </button>
                
                <button
                  disabled={pagination.current === 1}
                  onClick={() => pagination.onPageChange(pagination.current - 1)}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                
                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                  Page {pagination.current} of {Math.ceil(pagination.total / pagination.pageSize)}
                </span>
                
                <button
                  disabled={pagination.current * pagination.pageSize >= pagination.total}
                  onClick={() => pagination.onPageChange(pagination.current + 1)}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                
                <button
                  disabled={pagination.current * pagination.pageSize >= pagination.total}
                  onClick={() => pagination.onPageChange(Math.ceil(pagination.total / pagination.pageSize))}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;